"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { getAssistantRecordPath } from "@agroproj/backend/convex/assistant/routeContext";
import { useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import type { Route } from "next";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConvexAuthReady } from "@/hooks/use-convex-auth-ready";
import { AssistantProposalBoundary } from "./assistant-proposal-boundary";
export function AssistantProducerConfirmation(t0) {
  const $ = _c(6);
  const {
    proposalId,
    onNavigate
  } = t0;
  let t1;
  if ($[0] !== onNavigate || $[1] !== proposalId) {
    t1 = <ProducerReview proposalId={proposalId} onNavigate={onNavigate} />;
    $[0] = onNavigate;
    $[1] = proposalId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== proposalId || $[4] !== t1) {
    t2 = <AssistantProposalBoundary key={proposalId}>{t1}</AssistantProposalBoundary>;
    $[3] = proposalId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function ProducerReview({
  proposalId,
  onNavigate
}: {
  proposalId: string;
  onNavigate: () => void;
}) {
  const id = proposalId as Id<"assistant_producer_proposals">;
  const {
    canQuery
  } = useConvexAuthReady();
  const proposal = useQuery(api.assistant.producers.getProposal, canQuery ? {
    proposalId: id
  } : "skip");
  const confirm = useMutation(api.assistant.producers.confirm);
  const cancel = useMutation(api.assistant.producers.cancel);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiredAt, setExpiredAt] = useState<number>();
  const deadline = proposal?.status === "pending" ? proposal.expiresAt : undefined;
  useEffect(() => {
    if (deadline === undefined) return;
    const timeout = setTimeout(() => setExpiredAt(deadline), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timeout);
  }, [deadline]);
  async function submit(action: "confirm" | "cancel") {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (action === "confirm") await confirm({
        proposalId: id,
        confirmed: true
      });else await cancel({
        proposalId: id
      });
    } catch (cause) {
      setError(cause instanceof ConvexError && typeof cause.data === "string" ? cause.data : "Não foi possível concluir. Tente novamente.");
    } finally {
      setBusy(false);
    }
  }
  if (!proposal) return <p role="status" className="min-h-32">
        Carregando revisão...
      </p>;
  if (proposal.status === "confirmed" && proposal.producerId) {
    const path = getAssistantRecordPath({
      organizationSlug: proposal.organizationSlug,
      recordType: "producer",
      recordId: proposal.producerId
    });
    return <div role="status" className="space-y-2">
        <p>Produtor criado.</p>
        <Link className="underline underline-offset-4" href={path as Route} onClick={onNavigate}>
          Abrir produtor
        </Link>
      </div>;
  }
  if (proposal.status === "canceled") return <p role="status">Proposta cancelada.</p>;
  if (proposal.status === "expired" || proposal.expired || expiredAt === proposal.expiresAt) return <p role="status">Proposta expirada. Peça uma nova revisão.</p>;
  return <section aria-label="Revisão do produtor" className="mt-2 space-y-3 border-t pt-3">
      <p className="font-medium">Revise antes de criar o produtor</p>
      <dl className="space-y-1">
        {proposal.details.map(({
        label,
        value
      }) => <div key={label} className="flex gap-3">
            <dt className="text-muted-foreground w-24 shrink-0">{label}</dt>
            <dd className="min-w-0 break-words">{value}</dd>
          </div>)}
      </dl>
      {error ? <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert> : null}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" type="button" variant="ghost" disabled={busy} onClick={() => void submit("cancel")}>
          Cancelar
        </Button>
        <Button size="sm" type="button" disabled={busy} onClick={() => void submit("confirm")}>
          {busy ? "Processando..." : "Criar produtor"}
        </Button>
      </div>
    </section>;
}